import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import EditSquareIcon from "@mui/icons-material/EditSquare";
import HelpIcon from "@mui/icons-material/Help";
import { Box, Stack, styled, Tooltip } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useUserContext } from "@/contexts/UserContext/index.context";
import { useUpdateVehicle } from "@/features/delivery/hooks/use-delivery-actions";
import type { EndPointSectionFieldsFragment } from "@/libs/generated/operations";
import { UserRole } from "@/types/models/user.type";
import MiddleArrow from "./Arrow";
import PhotoModal from "./PhotoComposition/PhotoModal";
import PreviewPhotoModal from "./PhotoComposition/preview-photo-modal";
import VehicleDetails from "./VehicleDetails";

type Props = {
  delivery: EndPointSectionFieldsFragment;
  index?: number;
  isAdmin?: boolean;
};

const DeliveryDetailsBox = styled(Box)(({ theme }) => ({
  position: "relative",
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  background: theme.palette.background.paper,
  display: "flex",
  gap: theme.spacing(2.5),
  justifyContent: "space-between",
}));

function DeliveryDetailsSection({ delivery, index = 0 }: Props) {
  const { t } = useTranslation();
  const vehicle = delivery.vehicles[index];
  const { userState } = useUserContext();
  const isAdmin = userState?.["custom:role"] === UserRole.ADMIN;

  const { mutate: updateVehicle } = useUpdateVehicle();
  const [isEditing, setIsEditing] = useState(false);
  const [mileageBefore, setMileageBefore] = useState("");
  const [essenceBefore, setEssenceBefore] = useState("");
  const [mileageAfter, setMileageAfter] = useState("");
  const [essenceAfter, setEssenceAfter] = useState("");
  const [pickedUpAt, setPickedUpAt] = useState<Date | undefined>(undefined);
  const [deliveredAt, setDeliveredAt] = useState<Date | undefined>(undefined);

  const handleEditClick = () => {
    setMileageBefore(
      vehicle?.mileageBefore !== undefined &&
        vehicle?.mileageBefore !== null &&
        vehicle?.mileageBefore !== -1
        ? String(vehicle.mileageBefore)
        : ""
    );
    setEssenceBefore(
      vehicle?.essenceBefore !== undefined &&
        vehicle?.essenceBefore !== null &&
        vehicle?.essenceBefore !== -1
        ? String(vehicle.essenceBefore)
        : ""
    );
    setMileageAfter(
      vehicle?.mileageAfter !== undefined &&
        vehicle?.mileageAfter !== null &&
        vehicle?.mileageAfter !== -1
        ? String(vehicle.mileageAfter)
        : ""
    );
    setEssenceAfter(
      vehicle?.essenceAfter !== undefined &&
        vehicle?.essenceAfter !== null &&
        vehicle?.essenceAfter !== -1
        ? String(vehicle.essenceAfter)
        : ""
    );
    setPickedUpAt(vehicle?.actualPickedUpAt ? new Date(vehicle.actualPickedUpAt) : undefined);
    setDeliveredAt(vehicle?.actualArrivedAt ? new Date(vehicle.actualArrivedAt) : undefined);
    setIsEditing(true);
  };

  const handleSave = () => {
    updateVehicle(
      {
        updateInput: {
          id: vehicle.id,
          mileageBefore: mileageBefore !== "" ? Number(mileageBefore) : null,
          essenceBefore: essenceBefore !== "" ? Number(essenceBefore) : null,
          mileageAfter: mileageAfter !== "" ? Number(mileageAfter) : null,
          essenceAfter: essenceAfter !== "" ? Number(essenceAfter) : null,
          actualPickedUpAt: pickedUpAt || null,
          actualArrivedAt: deliveredAt || null,
        },
      },
      {
        onSuccess: () => {
          setIsEditing(false);
        },
      }
    );
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  return (
    <>
      <DeliveryDetailsBox>
        <Helper
          title={
            index === 0
              ? t("delivery.summary.delivered-vehicle-information")
              : t("delivery.summary.restituted-vehicle-information")
          }
        />
        {isEditing ? (
          <Stack
            direction="row"
            spacing={1}
            sx={{
              position: "absolute",
              top: "10px",
              right: "40px",
              zIndex: 2,
            }}
          >
            <CheckIcon onClick={handleSave} sx={{ cursor: "pointer" }} color="success" />
            <CloseIcon onClick={handleCancel} sx={{ cursor: "pointer" }} color="error" />
          </Stack>
        ) : (
          <EditSquareIcon
            onClick={handleEditClick}
            sx={{
              position: "absolute",
              top: "10px",
              right: "40px",
              cursor: "pointer",
              zIndex: 2,
            }}
            color="primary"
          />
        )}
        <Box
          sx={{
            width: "100%",
            display: "flex",
            gap: "16px",
            position: "relative",
            zIndex: 1,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <VehicleDetails
              title={t("common.shared.departed")}
              date={(() => {
                const pickedUp = delivery?.vehicles[index].pickedUpAt;
                const actual = delivery?.vehicles[index].actualPickedUpAt;
                return actual || pickedUp || undefined;
              })()}
              km={vehicle?.mileageBefore ?? -1}
              fuel={vehicle?.essenceBefore ?? -1}
              isEditing={isEditing}
              mileageValue={mileageBefore}
              onChangeMileage={setMileageBefore}
              fuelValue={essenceBefore}
              onChangeFuel={setEssenceBefore}
              dateValue={pickedUpAt}
              onChangeDate={setPickedUpAt}
            />
          </Box>
          <Box sx={{ display: "flex", justifyContent: "center", pt: "52px", minWidth: "40px" }}>
            <MiddleArrow />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <VehicleDetails
              title={t("common.shared.arrived")}
              date={(() => {
                const delivered = delivery?.vehicles[index].deliveredAt;
                const actual = delivery?.vehicles[index].actualArrivedAt;
                return actual || delivered || undefined;
              })()}
              fuel={vehicle?.essenceAfter ?? -1}
              km={vehicle?.mileageAfter ?? -1}
              isEditing={isEditing}
              mileageValue={mileageAfter}
              onChangeMileage={setMileageAfter}
              fuelValue={essenceAfter}
              onChangeFuel={setEssenceAfter}
              dateValue={deliveredAt}
              onChangeDate={setDeliveredAt}
            />
          </Box>
        </Box>
      </DeliveryDetailsBox>
      <Stack direction="row" spacing={2} sx={{ my: 2 }}>
        <PreviewPhotoModal delivery={delivery} index={index} />
        {isAdmin && <PhotoModal delivery={delivery} index={index} isAdmin={isAdmin} />}
      </Stack>
    </>
  );
}

function Helper({ title }: { title: string }) {
  return (
    <Tooltip
      sx={{
        position: "absolute",
        top: "10px",
        right: "10px",
        cursor: "pointer",
        zIndex: 2,
      }}
      title={title}
      placement="left"
    >
      <HelpIcon color="primary" />
    </Tooltip>
  );
}

export default DeliveryDetailsSection;
